// GroupAppAllowlistFromAlertModal — quick-suppress modal opened from the Detection
// Alerts Actions column (v1.85.1). Unlike the (user, application) modal, an alert
// carries a USER, not a group — and the group is the authoritative match key — so the
// analyst must PICK which Entra group to allowlist. We load the tenant's groups from
// the SilverM365EntraGroups ATS snapshot (today→yesterday fallback, like the manager
// page) and capture the verbatim display name; a manual-entry fallback covers an empty
// snapshot. The application side auto-fills from the alert (this app / any app). POSTs
// via addGroupAppAllowlistEntry, which gates on the rule's skip_if_in_group_app_allowlist
// toggle and bumps the version sentinel. Membership is refreshed once daily, so a
// newly-allowlisted group's members start being suppressed at the next daily run.

import React, { useState, useEffect, useMemo } from 'react';
import {
  XMarkIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { addGroupAppAllowlistEntry, fetchEntraIdentityInventory } from '../../api/client';
import SearchableSelect from '../ui/SearchableSelect';

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function GroupAppAllowlistFromAlertModal({ alert, onClose }) {
  const alertAppId = (alert.applicationId || '').trim();
  const alertAppName = (alert.appDisplayName || '').trim();
  const appIsValidGuid = GUID_REGEX.test(alertAppId);

  // Group: picked from the tenant's Entra groups (verbatim display name), or typed.
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsSnapshot, setGroupsSnapshot] = useState('');
  const [groupMode, setGroupMode] = useState('pick'); // 'pick' | 'manual'
  const [groupName, setGroupName] = useState('');

  // Default narrowest valid app suppression: the specific app when usable, else '*'.
  const [anyApp, setAnyApp] = useState(!appIsValidGuid);
  const [note, setNote] = useState('');
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [applied, setApplied] = useState(null);

  // Load the tenant's Entra groups (today→yesterday snapshot fallback).
  useEffect(() => {
    let cancelled = false;
    const tid = (alert.tenantId || '').trim();
    if (!tid) { setGroupMode('manual'); return; }
    setGroupsLoading(true);
    (async () => {
      try {
        const td = todayStr();
        let g = await fetchEntraIdentityInventory(tid, { snapshotDate: td, collection: 'groups' });
        let effectiveDate = td;
        if (!Array.isArray(g) || g.length === 0) {
          const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
          const g2 = await fetchEntraIdentityInventory(tid, { snapshotDate: yesterday, collection: 'groups' });
          if (Array.isArray(g2) && g2.length > 0) { g = g2; effectiveDate = yesterday; }
        }
        if (cancelled) return;
        const list = Array.isArray(g) ? g : [];
        setGroups(list);
        setGroupsSnapshot(list.length ? effectiveDate : '');
        if (list.length === 0) setGroupMode('manual');
      } catch {
        if (!cancelled) { setGroups([]); setGroupMode('manual'); }
      } finally {
        if (!cancelled) setGroupsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [alert.tenantId]);

  const sortedGroups = useMemo(
    () => [...groups]
      .filter((x) => x.displayName)
      .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '')),
    [groups]
  );

  const effectiveGroup = groupName.trim();
  const effectiveAppId = anyApp ? '*' : alertAppId.toLowerCase();

  const blocker = useMemo(() => {
    if (!alert.ruleId) return 'This alert has no rule id.';
    if (!alert.tenantId) return 'This alert has no tenant id.';
    if (!effectiveGroup) return 'Pick (or enter) the Entra group to allowlist.';
    if (effectiveGroup === '*') return "There is no 'any group' option — pick a specific group.";
    if (!anyApp && !appIsValidGuid) return 'The alert has no application GUID — choose "Any app (*)".';
    return null;
  }, [alert.ruleId, alert.tenantId, effectiveGroup, anyApp, appIsValidGuid]);

  const handleApply = async () => {
    if (blocker) { setError(blocker); return; }
    setApplying(true);
    setError(null);
    try {
      const res = await addGroupAppAllowlistEntry({
        ruleId:                 alert.ruleId,
        tenantId:               alert.tenantId,
        groupDisplayName:       effectiveGroup,
        applicationId:          effectiveAppId,
        applicationDisplayName: anyApp ? '' : alertAppName,
        note:                   note.trim(),
        enabled:                true,
      });
      setApplied({ pendingSync: !!res?.pendingSync, created: !!res?.created });
    } catch (err) {
      setError(err.message || 'Failed to add allowlist entry');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-lg w-full max-w-xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800">
          <div>
            <h2 className="text-lg font-semibold text-gray-100">Suppress (group, application) pair</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {alert.ruleName ? <span className="text-gray-400">{alert.ruleName}</span> : null}
              {' '}· Tenant <span className="font-mono">{alert.tenantId}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 p-1">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {applied ? (
            <div className="p-4 bg-emerald-900/20 border border-emerald-700/40 rounded-lg text-sm text-emerald-200 flex items-start gap-3">
              <CheckCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">{applied.created ? 'Pair allowlisted.' : 'Pair updated (already existed).'}</p>
                <p className="text-emerald-300/80 mt-1">
                  Members of <span className="font-mono">{effectiveGroup}</span> signing in via{' '}
                  <span className="font-mono">{effectiveAppId}</span> will no longer alert on this rule.
                  {applied.pendingSync && ' The sentinel bump is pending retry (~30 s); the pipeline will pick it up shortly.'}
                </p>
              </div>
            </div>
          ) : (
            <>
              <p className="text-xs text-gray-500">
                A match is suppressed when the signing user is a member of the chosen Entra group AND the application
                matches. The group is matched by its <strong>display name</strong>; the app by its <strong>GUID</strong>.
                Group membership is refreshed once daily. A known-malicious IP still alerts.
              </p>

              {/* Group */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                  Entra Group
                  <span className="text-gray-600 font-normal normal-case">
                    {groupsLoading ? ' — loading…' : groupsSnapshot ? ` — ${sortedGroups.length} groups (${groupsSnapshot})` : ' — no snapshot'}
                  </span>
                </label>
                <div className="flex items-center gap-3 mb-2 text-xs text-gray-400">
                  {[
                    { v: 'pick', label: 'Pick group' },
                    { v: 'manual', label: 'Enter name' },
                  ].map((m) => (
                    <label key={m.v} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="groupSelMode" value={m.v} checked={groupMode === m.v}
                        onChange={() => { setGroupMode(m.v); setGroupName(''); }}
                        className="border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500" />
                      {m.label}
                    </label>
                  ))}
                </div>
                {groupMode === 'pick' ? (
                  <SearchableSelect
                    value={groupName}
                    onChange={setGroupName}
                    options={sortedGroups.map((g) => ({ value: g.displayName, label: g.displayName }))}
                    disabled={groupsLoading || sortedGroups.length === 0}
                    placeholder={sortedGroups.length === 0 ? '— No groups in snapshot —' : 'Search groups…'}
                  />
                ) : (
                  <input
                    type="text"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="Verbatim Entra group display name"
                    className="input-field w-full text-xs"
                  />
                )}
              </div>

              {/* Application */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Application</label>
                <div className="space-y-1.5 text-sm">
                  <label className={`flex items-start gap-2 ${appIsValidGuid ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
                    <input type="radio" name="appSel" checked={!anyApp} disabled={!appIsValidGuid}
                      onChange={() => setAnyApp(false)}
                      className="border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500 mt-0.5" />
                    <span className="text-gray-300 min-w-0">
                      This app: <span className="text-gray-200">{alertAppName || <span className="italic text-gray-500">(no name)</span>}</span>
                      <span className="font-mono text-[11px] text-gray-500 block truncate">{alertAppId || '(no application GUID on this alert)'}</span>
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="appSel" checked={anyApp} onChange={() => setAnyApp(true)}
                      className="border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500" />
                    <span className="text-amber-300">Any app (*)</span>
                  </label>
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Note</label>
                <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="Why is this (group, app) pair benign?" className="input-field w-full" />
              </div>

              {error && (
                <div className="p-3 bg-red-900/30 border border-red-700/50 rounded text-red-300 text-sm flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              {!error && blocker && (
                <div className="p-3 bg-amber-900/20 border border-amber-700/40 rounded text-amber-300 text-xs flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                  <span>{blocker}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-800">
          {applied ? (
            <button onClick={onClose} className="btn-primary px-4 py-1.5 text-sm">Done</button>
          ) : (
            <>
              <button onClick={onClose} disabled={applying} className="btn-secondary px-4 py-1.5 text-sm">Cancel</button>
              <button onClick={handleApply} disabled={applying || !!blocker} className="btn-primary px-4 py-1.5 text-sm flex items-center gap-2">
                {applying && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                Allowlist pair
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
